import React from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import Rotate90DegreesCwOutlinedIcon from "@mui/icons-material/Rotate90DegreesCwOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

// La paleta de piezas: se elige un tipo de rack y después se hace clic en las
// celdas libres del plano para ir estampándolo.
//
// Es clic y no arrastre a propósito. Armar un pasillo son diez o veinte racks
// iguales; arrastrar cada uno desde la paleta hasta su celda es diez veces el
// trabajo de elegir la pieza una vez y hacer clic diez veces. El arrastre queda
// para lo que sí es arrastre: mover un rack que ya está puesto.
const MiniFootprint = ({ template, rotation }) => {
    const swap = rotation === 90 || rotation === 270;
    const cols = swap ? template.footprint_h : template.footprint_w;
    const rows = swap ? template.footprint_w : template.footprint_h;

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${cols}, 8px)`,
                gridTemplateRows: `repeat(${rows}, 8px)`,
                gap: "2px",
                flexShrink: 0,
            }}
        >
            {Array.from({ length: cols * rows }).map((_, index) => (
                <Box
                    key={index}
                    sx={{
                        bgcolor: template.color || "primary.main",
                        opacity: 0.7,
                        borderRadius: "1px",
                    }}
                />
            ))}
        </Box>
    );
};

const RackPalette = ({
    templates,
    zones,
    selectedTemplate,
    onSelectTemplate,
    rotation,
    onRotate,
    zoneId,
    onZoneChange,
    onManageTemplates,
    onManageZones,
}) => {
    return (
        <Paper variant="outlined" sx={{ p: 2, width: 280, flexShrink: 0, alignSelf: "flex-start" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Piezas</Typography>
                <Tooltip title="Administrar tipos de rack">
                    <IconButton size="small" onClick={onManageTemplates}>
                        <SettingsOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            {templates.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Todavía no hay tipos de rack. Creá el primero para poder armar el plano.
                </Alert>
            ) : (
                <Stack spacing={1} sx={{ mb: 2 }}>
                    {templates.map((template) => {
                        const active = selectedTemplate?.id === template.id;
                        return (
                            <Paper
                                key={template.id}
                                variant="outlined"
                                onClick={() => onSelectTemplate(active ? null : template)}
                                sx={{
                                    p: 1,
                                    cursor: "pointer",
                                    borderColor: active ? "primary.main" : "divider",
                                    borderWidth: active ? 2 : 1,
                                    bgcolor: active ? "action.selected" : "transparent",
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <MiniFootprint template={template} rotation={active ? rotation : 0} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" noWrap sx={{ fontWeight: active ? 600 : 400 }}>
                                            {template.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {template.levels} niveles × {template.positions} ={" "}
                                            {template.positions_count} pos.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            )}

            {selectedTemplate && (
                <>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Rotate90DegreesCwOutlinedIcon />}
                            onClick={onRotate}
                        >
                            Rotar
                        </Button>
                        <Chip size="small" label={`${rotation}°`} />
                    </Stack>

                    <Alert severity="success" sx={{ mb: 2, py: 0.5 }}>
                        <Typography variant="caption">
                            Hacé clic en una celda libre del plano para poner el rack. Volvé a clickear la pieza para
                            soltarla.
                        </Typography>
                    </Alert>
                </>
            )}

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Zona</Typography>
                <Tooltip title="Administrar zonas">
                    <IconButton size="small" onClick={onManageZones}>
                        <SettingsOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            {zones.length === 0 ? (
                <Alert severity="info">
                    Sin zonas todavía. Los racks se pueden poner igual, pero sin zona no entran en el orden del
                    recorrido por zonas.
                </Alert>
            ) : (
                <TextField
                    select
                    fullWidth
                    size="small"
                    label="Dónde se pone"
                    value={zoneId || ""}
                    onChange={(event) => onZoneChange(event.target.value || null)}
                >
                    <MenuItem value="">Sin zona</MenuItem>
                    {zones.map((zone) => (
                        <MenuItem key={zone.id} value={zone.id}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "2px",
                                        bgcolor: zone.color || "grey.400",
                                    }}
                                />
                                <span>
                                    {zone.code} · {zone.name}
                                </span>
                            </Stack>
                        </MenuItem>
                    ))}
                </TextField>
            )}
        </Paper>
    );
};

export default RackPalette;
